/**
 * AgentAccountPanel — the "My Account" tab body extracted from the
 * monolithic PropertyAdminDashboard.
 *
 * Renders the agent's profile form (name, logo, contact details,
 * WhatsApp, address, email-notifications toggle, notes) with per-field
 * visibility gated by the CMS `useCmsFieldVisibility` hook.
 *
 * State + handlers stay on the parent so autosave + the shared save
 * flow don't have to be duplicated. Everything is passed as props.
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { useCmsFieldVisibility } from "@/hooks/useCmsFieldVisibility";
import { resolveImageUrl } from "@/utils/imageUrl";

export default function AgentAccountPanel({
  agentInfo,
  username,
  profileForm,
  onFieldChange,
  onSave,
  saving,
  hasChanges,
  lastSaved,
}) {
  const vis = useCmsFieldVisibility();

  return (
    <div className="bg-white border border-border p-6 space-y-6" data-testid="account-tab-content">
      <div className="border-b border-border pb-4">
        <h3 className="font-serif text-xl font-medium">Account Profile</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Edit your account details. These appear alongside your property listings.
        </p>
      </div>

      {/* Login Details (read-only) */}
      <div className="space-y-3">
        <h4 className="font-medium text-sm">Signed in as</h4>
        <div className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-3 items-center max-w-md text-sm">
          <Label className="text-xs text-muted-foreground">Email</Label>
          <span>{agentInfo?.google_email || username || "—"}</span>
          <Label className="text-xs text-muted-foreground">Auth</Label>
          <span className="text-muted-foreground">Google SSO</span>
        </div>
      </div>

      {/* Profile form — mirrors master admin Edit Agent fields (no password) */}
      <div className="border-t border-border pt-6 space-y-5">
        <h4 className="font-medium text-sm">Account Details</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vis.shouldShow("agent_name", "user") && (
          <div className="space-y-2 md:col-span-2">
            <Label>Full name *</Label>
            <Input
              value={profileForm.name}
              onChange={(e) => onFieldChange("name", e.target.value)}
              placeholder="ABC Realty"
              className="rounded-none"
              data-testid="profile-name"
            />
          </div>
          )}

          {vis.shouldShow("agent_logo", "user") && (
          <div className="space-y-2 md:col-span-2">
            <Label>Account Logo</Label>
            <ImageUpload
              onUpload={(url) => onFieldChange("logo", url)}
              label="Upload Account Logo"
              skipWatermark={true}
            />
            {profileForm.logo && (
              <div className="flex items-center gap-3 mt-2 p-2 border border-border bg-muted/20">
                <img
                  src={resolveImageUrl(profileForm.logo)}
                  alt="Account logo preview"
                  className="h-12 w-auto max-w-[120px] object-contain bg-white p-1 border border-border"
                />
                <button
                  type="button"
                  onClick={() => onFieldChange("logo", "")}
                  className="text-xs text-red-600 hover:underline"
                  data-testid="profile-logo-remove"
                >
                  Remove logo
                </button>
              </div>
            )}
            <Input
              placeholder="https://example.com/logo.png"
              value={profileForm.logo || ""}
              onChange={(e) => onFieldChange("logo", e.target.value)}
              className="rounded-none mt-2"
              data-testid="profile-logo-url"
            />
          </div>
          )}

          {vis.shouldShow("agent_contact_name", "user") && (
          <div className="space-y-2">
            <Label>Contact Name</Label>
            <Input
              value={profileForm.contact_name}
              onChange={(e) => onFieldChange("contact_name", e.target.value)}
              placeholder="John Smith"
              className="rounded-none"
              data-testid="profile-contact-name"
            />
          </div>
          )}

          {vis.shouldShow("agent_email", "user") && (
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={profileForm.email}
              onChange={(e) => onFieldChange("email", e.target.value)}
              placeholder="you@example.com"
              className="rounded-none"
              data-testid="profile-email"
            />
          </div>
          )}

          {vis.shouldShow("agent_tel", "user") && (
          <div className="space-y-2">
            <Label>Your Telephone</Label>
            <Input
              value={profileForm.phone}
              onChange={(e) => onFieldChange("phone", e.target.value)}
              placeholder="+44 7123 456789"
              className="rounded-none"
              data-testid="profile-phone"
            />
            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground select-none pt-1">
              <Switch
                checked={profileForm.show_phone_in_modal !== false}
                onCheckedChange={(v) => onFieldChange("show_phone_in_modal", v)}
                data-testid="profile-show-phone-in-modal"
              />
              Show telephone number on property quick-view modal
            </label>
          </div>
          )}

          <div className="space-y-2">
            <Label>WhatsApp Number (optional)</Label>
            <Input
              value={profileForm.whatsapp_phone || ""}
              onChange={(e) => onFieldChange("whatsapp_phone", e.target.value)}
              placeholder="Same as telephone if left blank"
              className="rounded-none"
              data-testid="profile-whatsapp-phone"
            />
            <p className="text-xs text-muted-foreground">
              Powers the &quot;Chat on WhatsApp&quot; button on your property pages. Use the international format (e.g. +447123456789).
            </p>
          </div>

          <div className="md:col-span-2 flex items-center gap-2">
            <Switch
              checked={profileForm.whatsapp_enabled !== false}
              onCheckedChange={(v) => onFieldChange("whatsapp_enabled", v)}
              data-testid="profile-whatsapp-enabled"
            />
            <div className="flex-1">
              <Label>Accept WhatsApp communication</Label>
              <p className="text-xs text-muted-foreground">
                When off, the &quot;Chat on WhatsApp&quot; button is hidden from all your property pages.
              </p>
            </div>
          </div>

          {vis.shouldShow("agent_website", "user") && (
          <div className="space-y-2">
            <Label>Your Website</Label>
            <Input
              value={profileForm.website}
              onChange={(e) => onFieldChange("website", e.target.value)}
              placeholder="https://www.example.com"
              className="rounded-none"
              data-testid="profile-website"
            />
          </div>
          )}

          {vis.shouldShow("agent_address", "user") && (
          <div className="space-y-2 md:col-span-2">
            <Label>Address</Label>
            <Input
              value={profileForm.address}
              onChange={(e) => onFieldChange("address", e.target.value)}
              placeholder="123 High Street, London"
              className="rounded-none"
              data-testid="profile-address"
            />
          </div>
          )}

          {vis.shouldShow("agent_email_notifications", "user") && (
          <div className="md:col-span-2 flex items-center gap-2">
            <Switch
              checked={profileForm.email_notifications}
              onCheckedChange={(checked) => onFieldChange("email_notifications", checked)}
              data-testid="profile-email-notifications"
            />
            <Label>Email Notifications (receive enquiry alerts)</Label>
          </div>
          )}

        </div>
      </div>

      {/* Communication Preferences — controls which non-essential emails
          the agent receives. Transactional emails (enquiry alerts, offer
          notifications, login credentials) always send regardless. */}
      <div className="border-t border-border pt-6 space-y-3" data-testid="communication-preferences-section">
        <h4 className="font-medium text-sm">Communication Preferences</h4>
        <p className="text-xs text-muted-foreground">
          Choose which optional emails you'd like to receive. Enquiry and account alerts are unaffected.
        </p>
        <div className="flex items-center gap-2">
          <Switch
            checked={profileForm.marketing_emails !== false}
            onCheckedChange={(v) => onFieldChange("marketing_emails", v)}
            data-testid="profile-marketing-emails"
          />
          <Label>Marketing emails (campaigns, promos and new feature updates)</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={profileForm.supplier_emails !== false}
            onCheckedChange={(v) => onFieldChange("supplier_emails", v)}
            data-testid="profile-supplier-emails"
          />
          <Label>Supplier emails (updates from preferred suppliers)</Label>
        </div>
      </div>

      <div className="border-t border-border pt-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vis.shouldShow("agent_notes", "user") && (
          <div className="space-y-2 md:col-span-2">
            <Label>Account Notes (Internal)</Label>
            <Textarea
              value={profileForm.notes}
              onChange={(e) => onFieldChange("notes", e.target.value)}
              placeholder="Internal notes (not visible to customers)..."
              className="rounded-none min-h-20"
              data-testid="profile-notes"
            />
          </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="profile-autosave-status">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : hasChanges ? (
              <span>Unsaved changes...</span>
            ) : lastSaved ? (
              <span>All changes saved</span>
            ) : (
              <span>Auto-save enabled</span>
            )}
          </div>
          <Button
            onClick={onSave}
            disabled={saving}
            className="rounded-none"
            data-testid="profile-save-btn"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Profile
          </Button>
        </div>
      </div>
    </div>
  );
}
